import React from 'react';
import {
    AccuracyConfirmation,
    PositionCommitments,
    RequirementAnswers,
} from '@/features/applications/student/blocks';
import { getErrors as commitmentErrors } from '@/features/applications/student/blocks/PositionCommitments';
import { getErrors as courseErrors } from '@/features/applications/student/blocks/RequirementAnswers';
import ProgramCommitments, {
    applicableCommitments,
} from '@/features/applications/student/programs/ProgramCommitments';
import YesNoQuestion from '@/features/applications/student/programs/YesNoQuestion';
import { AttendanceRefusal } from '@/features/applications/student/programs/LaEligibility';
import {
    LA_ERROR_MESSAGES,
    LA_IDS,
    LA_QUESTIONS,
} from '@/features/applications/student/programs/laAnswers';
import { getProgramDefinition } from '@/features/applications/student/programs';
import { requirementAttestationsBody } from '@/shared/utils/requirementAnswers';

/**
 * What a nominated student answers beside the employment questions before
 * they accept: the same program terms a student who applied gives. The
 * screens are the application wizard's own blocks, fed by the offer the
 * server sends with the nomination (`termsOffer`), so the questions and the
 * wording come from the program's configuration and cannot drift from the
 * application's.
 */

export const EMPTY_NOMINEE_TERMS = Object.freeze({
    requirementAnswers: Object.freeze({
        courses: Object.freeze({}),
        commitments: Object.freeze({}),
    }),
    isReturning: null,
    canAttendOrientation: null,
    canAttendPedagogy: null,
    commitmentsAcknowledged: false,
    confirmsSubmissionAccuracy: false,
});

const roleNameOf = (nomination) =>
    getProgramDefinition(nomination.programType)?.roleName ||
    {
        la: 'Learning Assistant',
        si: 'SI Leader',
        ta: 'Teaching Assistant',
    }[nomination.programType] ||
    'peer supporter';

// The new-or-returning answer that picks the commitments: the server's when
// it already knows (the student's application), else the nominee's own.
const returningOf = (offer, terms) =>
    typeof offer.isReturning === 'boolean'
        ? offer.isReturning
        : terms.isReturning;

// A new Learning Assistant answers the orientation and the pedagogy seminar,
// as a new LA applicant does.
const asksAttendance = (offer, returning) =>
    offer.asksAttendance === true && returning === false;

// The wizard's blocks read one answers object; this is the nominee's.
function wizardAnswers(nomination, terms) {
    const offer = nomination.termsOffer;
    return {
        positions: [
            {
                applicationId: offer.position,
                summary: {
                    ...nomination.position,
                    hoursPerWeek: nomination.hoursPerWeek,
                },
            },
        ],
        requirementAnswers: terms.requirementAnswers,
        programAnswers: { isReturning: returningOf(offer, terms) === true },
        confirmsSubmissionAccuracy: terms.confirmsSubmissionAccuracy,
    };
}

const rulesOf = (offer) => ({
    assertions: { priorCompletion: offer.asksPriorCompletion },
});

/** What is still to answer, as labels, for the hint beside Accept. */
export function openTermsItems(nomination, terms) {
    const offer = nomination.termsOffer;
    if (!offer) return ['the program terms'];
    const answers = wizardAnswers(nomination, terms);
    const open = [
        ...courseErrors(answers, rulesOf(offer)).map(
            () => 'the course question'
        ),
        ...commitmentErrors(answers).map(() => 'the meeting times and hours'),
    ];
    const returning = returningOf(offer, terms);
    if (offer.asksIsReturning && typeof returning !== 'boolean') {
        open.push('whether you have held the role before');
    }
    if (
        applicableCommitments(offer.commitments, returning).length > 0 &&
        terms.commitmentsAcknowledged !== true
    ) {
        open.push('the commitments');
    }
    if (asksAttendance(offer, returning)) {
        const {
            canAttendOrientation: orientation,
            canAttendPedagogy: seminar,
        } = terms;
        if (orientation === false || seminar === false) {
            // A No is an answer, and the one the program does not accept
            open.push(
                'a Yes to the orientation and pedagogy seminar questions'
            );
        } else if (
            typeof orientation !== 'boolean' ||
            typeof seminar !== 'boolean'
        ) {
            open.push('the orientation and pedagogy seminar questions');
        }
    }
    if (terms.confirmsSubmissionAccuracy !== true) {
        open.push('the accuracy confirmation');
    }
    return [...new Set(open)];
}

export const isNomineeTermsComplete = (nomination, terms) =>
    openTermsItems(nomination, terms).length === 0;

/** The fields of the accept request that carry the terms. */
export function nomineeTermsBody(nomination, terms) {
    const offer = nomination.termsOffer;
    const returning = returningOf(offer, terms);
    return {
        requirementAttestations: requirementAttestationsBody(
            terms.requirementAnswers,
            { priorCompletion: offer.asksPriorCompletion },
            [offer.position]
        ),
        commitments: { acknowledged: terms.commitmentsAcknowledged === true },
        ...(offer.asksIsReturning &&
            typeof returning === 'boolean' && { isReturning: returning }),
        ...(asksAttendance(offer, returning) && {
            canAttendOrientation: terms.canAttendOrientation,
            canAttendPedagogy: terms.canAttendPedagogy,
        }),
        confirmsAccuracy: terms.confirmsSubmissionAccuracy === true,
    };
}

function NomineeTermsConfirm({ nomination, value, onChange }) {
    const offer = nomination.termsOffer;
    if (!offer) return null;
    const answers = wizardAnswers(nomination, value);
    const returning = returningOf(offer, value);
    // With one list for everyone the answer picks nothing, so the list shows.
    const listReturning = offer.asksIsReturning
        ? returning
        : (returning ?? false);
    const roleName = roleNameOf(nomination);
    const context = {
        positionRows: { [offer.position]: answers.positions[0].summary },
        trainingSessions: offer.trainingSessions,
        programRules: rulesOf(offer),
        nominee: true,
    };
    const set = (key, next) => {
        if (key === 'requirementAnswers') {
            onChange({ ...value, requirementAnswers: next });
        } else if (key === 'confirmsSubmissionAccuracy') {
            onChange({ ...value, confirmsSubmissionAccuracy: next });
        }
    };

    return (
        <div className="mt-4">
            <h3 className="h5">Program terms</h3>
            <p className="text-muted small">
                {`Every ${roleName} agrees to these terms, whether they applied or were named by a faculty member.`}
            </p>
            <RequirementAnswers
                answers={answers}
                onChange={set}
                context={context}
            />
            <PositionCommitments
                answers={answers}
                onChange={set}
                context={context}
                single
            />
            {offer.asksIsReturning && (
                <YesNoQuestion
                    id={LA_IDS.isReturning}
                    legend={
                        nomination.programType === 'la'
                            ? LA_QUESTIONS.isReturning
                            : 'Have you held this role at MSU Denver before?'
                    }
                    value={value.isReturning}
                    onChange={(next) =>
                        onChange({
                            ...value,
                            isReturning: next,
                            canAttendOrientation: null,
                            canAttendPedagogy: null,
                            commitmentsAcknowledged: false,
                        })
                    }
                />
            )}
            {asksAttendance(offer, returning) && (
                <>
                    <YesNoQuestion
                        id="nominee-can-attend-orientation"
                        legend={LA_QUESTIONS.canAttendOrientation}
                        value={value.canAttendOrientation}
                        onChange={(next) =>
                            onChange({ ...value, canAttendOrientation: next })
                        }
                        invalid={value.canAttendOrientation === false}
                        message={
                            value.canAttendOrientation === false ? (
                                <AttendanceRefusal nominee />
                            ) : (
                                LA_ERROR_MESSAGES[LA_IDS.canAttendOrientation]
                            )
                        }
                    />
                    <YesNoQuestion
                        id="nominee-can-attend-pedagogy"
                        legend={LA_QUESTIONS.canAttendPedagogy}
                        value={value.canAttendPedagogy}
                        onChange={(next) =>
                            onChange({ ...value, canAttendPedagogy: next })
                        }
                        invalid={value.canAttendPedagogy === false}
                        message={
                            value.canAttendPedagogy === false ? (
                                <AttendanceRefusal nominee />
                            ) : (
                                LA_ERROR_MESSAGES[LA_IDS.canAttendPedagogy]
                            )
                        }
                    />
                </>
            )}
            <ProgramCommitments
                id="nominee-commitments-acknowledged"
                roleName={roleName}
                prompt="Answer the question above to see the commitments that apply to you."
                requirements={{
                    requirements: { studentCommitments: offer.commitments },
                    loading: false,
                    loadError: false,
                    noTerm: false,
                    retry: () => {},
                }}
                requiredNote="Required to accept."
                isReturning={listReturning}
                checked={value.commitmentsAcknowledged}
                onChange={(next) =>
                    onChange({ ...value, commitmentsAcknowledged: next })
                }
            />
            <AccuracyConfirmation
                answers={answers}
                onChange={set}
                context={context}
            />
        </div>
    );
}

export default NomineeTermsConfirm;
